import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Popover,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  SmartToy as BotIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DocumentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import type { Message, Chunk } from '../types';

interface ChatMessageProps {
  message: Message;
}

// Deduplicate chunks by file_name, merging page ranges for the same document.
// Each document appears only once in the UI; if it spans multiple page ranges,
// they are merged into a single min-max range.
const getUniqueChunks = (chunks: Chunk[]): Chunk[] => {
  const fileMap = new Map<string, Chunk>();

  for (const chunk of chunks) {
    const key = chunk.file_name;
    if (!fileMap.has(key)) {
      fileMap.set(key, { ...chunk });
    } else {
      // Merge page ranges: keep the widest span
      const existing = fileMap.get(key)!;
      const existStart = existing.start_page ?? existing.page_number ?? 0;
      const existEnd = existing.end_page ?? existing.page_number ?? 0;
      const newStart = chunk.start_page ?? chunk.page_number ?? 0;
      const newEnd = chunk.end_page ?? chunk.page_number ?? 0;
      existing.start_page = Math.min(existStart, newStart);
      existing.end_page = Math.max(existEnd, newEnd);
      // Keep highest relevance score
      const existScore = existing.score ?? (existing as any)['@search.score'] ?? 0;
      const newScore = chunk.score ?? (chunk as any)['@search.score'] ?? 0;
      if (newScore > existScore) existing.score = newScore;
    }
  }

  return Array.from(fileMap.values());
};

// Format field name for display (snake_case to Title Case)
const formatFieldName = (field: string): string => {
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Get metadata fields from chunk sorted alphabetically
const getMetadataFields = (chunk: Chunk): { label: string; value: string }[] => {
  const metadataKeys: (keyof Chunk)[] = [
    // 'approval_status',  // hidden from UI
    'business_function',
    // 'document_approver',
    // 'document_owner',   // hidden from UI
    'document_status',
    'document_type',
    'internal_version_number',
    // 'program_type',
    'published_date',
    // 'quick_reference_categories',
  ];

  return metadataKeys
    .filter(key => chunk[key] && String(chunk[key]).trim() !== '')
    .map(key => ({
      label: formatFieldName(key),
      value: String(chunk[key]),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const rawChunks = message.metadata?.chunks || [];
  // Get unique chunks (count controlled by backend DEFAULT_NUM_CHUNKS)
  const chunks = getUniqueChunks(rawChunks);
  const hasChunks = chunks.length > 0;

  // Debug logging
  console.log('ChatMessage render:', {
    role: message.role,
    hasMetadata: !!message.metadata,
    rawChunksCount: rawChunks.length,
    filteredChunksCount: chunks.length,
    hasChunks,
    chunks: chunks,
  });

  // State for metadata popover
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedChunkMetadata, setSelectedChunkMetadata] = useState<{ label: string; value: string }[]>([]);
  const [legacyLinkNotice, setLegacyLinkNotice] = useState(false);

  const handleMetadataClick = (event: React.MouseEvent<HTMLElement>, chunk: Chunk) => {
    event.stopPropagation(); // Prevent document click
    const metadata = getMetadataFields(chunk);
    setSelectedChunkMetadata(metadata);
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedChunkMetadata([]);
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
      onClick={handlePopoverClose} // Close popover when clicking on chat area
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          maxWidth: '80%',
        }}
      >
        <Avatar
          sx={{
            bgcolor: isUser ? 'primary.main' : 'secondary.main',
            m: 1,
          }}
        >
          {isUser ? <PersonIcon /> : <BotIcon />}
        </Avatar>

        <Paper
          elevation={1}
          sx={{
            p: 2,
            bgcolor: isUser ? 'primary.light' : 'background.paper',
            color: isUser ? 'primary.contrastText' : 'text.primary',
          }}
        >
          <Box sx={{ mb: hasChunks ? 1 : 0 }}>
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              opacity: 0.7,
            }}
          >
            {new Date(message.timestamp).toLocaleTimeString()}
          </Typography>

          {hasChunks && (
            <Accordion
              sx={{ mt: 2, bgcolor: 'rgba(0, 0, 0, 0.03)' }}
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DocumentIcon fontSize="small" />
                  <Typography variant="body2" fontWeight="medium">
                    {chunks.length} source reference{chunks.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.7 }}>
                  Click on a source to view the document
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {chunks.map((chunk, index) => {
                    const metadataFields = getMetadataFields(chunk);
                    const hasMetadata = metadataFields.length > 0;

                    return (
                      <Paper
                        key={chunk.chunk_id || index}
                        elevation={1}
                        sx={{
                          p: 1.5,
                          cursor: chunk.sas_url ? 'pointer' : 'default',
                          '&:hover': chunk.sas_url ? {
                            bgcolor: 'action.hover',
                          } : {},
                          transition: 'background-color 0.2s',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!chunk.sas_url) return;
                          // Legacy citations from chats created before the
                          // /api/document proxy fix point at the storage
                          // account with a SAS token. Those SAS tokens are
                          // expired and the storage is locked down post-
                          // Phase-4, so the browser would otherwise render
                          // an Azure XML error. Intercept and show a
                          // friendly notice instead.
                          if (chunk.sas_url.includes('blob.core.windows.net')) {
                            setLegacyLinkNotice(true);
                            return;
                          }
                          window.open(chunk.sas_url, '_blank');
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <DocumentIcon fontSize="small" color="primary" sx={{ mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {chunk.file_name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                              {/* Page number display hidden
                              {(chunk.start_page || chunk.page_number) && (
                                <Typography variant="caption" color="text.secondary">
                                  • Page{chunk.end_page && chunk.end_page !== chunk.start_page ? 's' : ''}{' '}
                                  {chunk.start_page ?? chunk.page_number}
                                  {chunk.end_page && chunk.end_page !== chunk.start_page ? `–${chunk.end_page}` : ''}
                                </Typography>
                              )}
                              */}
                              {chunk.score !== undefined && (
                                <Typography variant="caption" color="text.secondary">
                                  • Relevance: {(chunk.score * 100).toFixed(0)}%
                                </Typography>
                              )}
                              {/* Metadata Link */}
                              {hasMetadata && (
                                <Link
                                  component="button"
                                  variant="caption"
                                  onClick={(e) => handleMetadataClick(e, chunk)}
                                  sx={{
                                    ml: 1,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                      color: 'primary.dark',
                                    },
                                  }}
                                >
                                  Metadata
                                </Link>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Metadata Popover */}
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClick={(e) => e.stopPropagation()}
            slotProps={{
              paper: {
                sx: {
                  width: '25vw',
                  minWidth: 280,
                  maxWidth: 400,
                  maxHeight: '25vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                },
              },
            }}
          >
            {/* Header with close button */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Document Metadata
              </Typography>
              <IconButton
                size="small"
                onClick={handlePopoverClose}
                sx={{
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
                aria-label="Close metadata"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {/* Scrollable content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 1.5,
                pt: 1,
              }}
            >
              {selectedChunkMetadata.length > 0 ? (
                <List dense disablePadding>
                  {selectedChunkMetadata.map((meta, idx) => (
                    <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {meta.label}:
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'right' }}>
                              {meta.value}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No metadata available
                </Typography>
              )}
            </Box>
          </Popover>
        </Paper>
      </Box>
      <Snackbar
        open={legacyLinkNotice}
        autoHideDuration={6000}
        onClose={() => setLegacyLinkNotice(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setLegacyLinkNotice(false)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          This source link is from an earlier chat session and is no longer valid.
          Please re-ask your question in a new chat to get a fresh link.
        </Alert>
      </Snackbar>
    </Box>
  );
};
